import React, { useContext, useState } from 'react';

import AnimateHeight from 'react-animate-height';
import { Container } from '~/components/layout/PageStructure';
import Icons from '../ui/Icons';
import Link from '~/components/ui/Link';
import PropTypes from 'prop-types';
import UIContext from '~/context/UIContext';
import cn from 'classnames';
import { ResponsiveImage } from '~/components/ui/molecules/ResponsiveImage';

// KeyboardEvent.key codes.
const KEY_TAB = 'Tab';
const KEY_SPACE = ' ';
const KEY_ARROW_DOWN = 'ArrowDown';

// KeyboardEvent.which codes (technically deprecated, but included just in case).
const WHICH_TAB = 9;
const WHICH_SPACE = 32;
const WHICH_ARROW_DOWN = 40;

/**
 * Return true if array has sub menu with links
 * @param {array} linklist
 */
const hasChildMenu = linklist => {
  return linklist.links?.length > 0;
};

/**
 * Return a nav item based on breakpoint and nav depth
 * @param {object} link
 * @param {depth} number
 * @param {maxDepth} number
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
const NavItem = ({ link, depth = 1, maxDepth = 4, setMenuOpen }) => {
  const [open, setOpen] = useState(false);
  const [fading, setFading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { breakpoint } = useContext(UIContext);
  const childLinks = link.links;
  const isDropdown = childLinks?.length > 0;
  const isMeganav = childLinks?.some(hasChildMenu);
  let singleColumn = [];
  let megaNavColumns = [];

  if (childLinks) {
    singleColumn = childLinks.filter(
      childLink => childLink.links && !childLink.links.length,
    );

    megaNavColumns = childLinks.filter(
      childLink => childLink.links && childLink.links.length,
    );
  }

  /**
   * Expand/contract the child menu for this NavItem. When contrating, slightly delay
   * updating the expanded state to give the child menu 150ms to fade out.
   *
   * @param {bool} isExpanding true if the menu should expand; false if it should contract.
   */
  const expandChildMenu = isExpanding => {
    if (isExpanding) {
      setExpanded(true);
    } else {
      setTimeout(() => {
        setExpanded(false);
      }, 150);
    }

    setFading(isExpanding);
  };

  /**
   * Manage the expanded state of the child menu for this NavItem based on mouse events.
   */
  const onHover = e => {
    expandChildMenu(e.type === 'mouseenter');
  };

  /**
   * Called on the top-level dropdown links to make the header nav keyboard-navigable.
   * Space will expand/contract the dropdown, and the down arrow will expand it (but
   * not contract it). Once visible, users can continue hitting tab to move through the
   * child items. Hitting shift + tab will close the menu.
   *
   * @param {KeyboardEvent} e The keydown event.
   */
  const expandHandler = e => {
    if (!isDropdown) return;

    // Shift + tab: close the menu.
    if (
      expanded &&
      e.shiftKey &&
      (e.key === KEY_TAB || e.which === WHICH_TAB)
    ) {
      expandChildMenu(false);
      return;
    }

    let isExpanded = null;

    if (e.key === KEY_SPACE || e.which === WHICH_SPACE) {
      // Space will swap the expanded state.
      e.preventDefault();
      isExpanded = !expanded;
    } else if (e.key === KEY_ARROW_DOWN || e.which === WHICH_ARROW_DOWN) {
      // The down arrow will only expand the menu.
      e.preventDefault();
      // noinspection PointlessBooleanExpressionJS
      if (!isExpanded) {
        isExpanded = true;
      }
    }

    if (isExpanded !== null) {
      expandChildMenu(isExpanded);
    }
  };

  /**
   * Part of header nav keyboard-navigability. When the user's tabbing through an
   * expanded menu, this will be called on the final child item, and will contract
   * the menu.
   *
   * @param {KeyboardEvent} e The keydown event.
   */
  const tabOutHandler = e => {
    // Bail if shift is held down to allow backwards traversal through child items.
    if (
      !expanded ||
      e.shiftKey ||
      !(e.key === KEY_TAB || e.which === WHICH_TAB)
    ) {
      return;
    }

    expandChildMenu(false);
  };

  if (depth === maxDepth) {
    return null;
  }

  return !breakpoint.isDesktop ? (
    <li
      className={cn('header-nav-item flex flex-wrap', {
        'ml-3': depth === 2,
        'ml-2': depth === 3,
      })}
    >
      <div className="flex w-full justify-between">
        <Link
          to={link.uri}
          className={cn(
            'w-1/2 flex-grow mr-2 text-nav pt-4 pb-3 inline-block',
            {
              uppercase: depth !== 3,
            },
          )}
          onClick={() => setMenuOpen(false)}
        >
          {link.title}
        </Link>
        {isDropdown && (
          <button
            className={cn('w-7 h-7 transform focus:ring-grey', {
              'rotate-180': open,
            })}
            onClick={() => setOpen(!open)}
          >
            <Icons type="nav" className="inline w-3" ariaLabel="Open sub nav" />
          </button>
        )}
      </div>

      <AnimateHeight height={open ? 'auto' : 0} className={'w-full'}>
        {isDropdown && (
          <ul className="header-nav-child w-full">
            {childLinks.map(childlink => (
              <NavItem
                key={`${link.id}-child-${childlink.id}`}
                link={childlink}
                depth={depth + 1}
                setMenuOpen={setMenuOpen}
              />
            ))}
          </ul>
        )}
      </AnimateHeight>
    </li>
  ) : (
    <li
      className={cn(
        'header-nav-item mx-3',
        isDropdown && !isMeganav && 'relative',
        fading && 'fading',
        expanded && 'expanded',
      )}
      onMouseEnter={onHover}
      onMouseLeave={onHover}
    >
      <Link
        to={link.uri}
        className="text-nav lg:text-nav-lg inline-block my-4 border-transparent hover:border-current uppercase"
        onKeyDown={expandHandler}
      >
        {link.title}
      </Link>
      {isDropdown && (
        <Icons type="nav" className="inline w-2 ml-2" ariaLabel="Sub nav" />
      )}

      {isDropdown && !isMeganav && (
        <ul
          className={cn(
            'header-nav-child header-nav-child--dropdown absolute top-full flex-col px-4 py-3 -ml-4 bg-main-0 border border-grey',
            {
              'pointer-events-none': !expanded,
            },
          )}
        >
          {childLinks.map((childlink, i) => (
            <li
              key={`${link.id}-dropdown-child-${childlink.id}`}
              className="my-1"
            >
              <Link
                to={childlink.uri}
                className="lg:text-nav-lg min-w-nav uppercase"
                onKeyDown={e => {
                  if (i === childLinks.length - 1) {
                    tabOutHandler(e);
                  }
                }}
              >
                {childlink.title}
              </Link>
            </li>
          ))}
        </ul>
      )}
      {isMeganav && (
        <div
          className={cn(
            'header-nav-child lg:absolute lg:w-100 lg:left-0 lg:right-0 lg:bg-main-0 pt-3',
            {
              'pointer-events-none': !expanded,
            },
          )}
        >
          <Container
            className={cn('flex justify-start flex-wrap', {
              'justify-start': childLinks.length > 3,
              'justify-center ': childLinks.length < 4,
            })}
          >
            {link.image?.url && (
              // I don't think this is used anywhere, but leaving for now
              <div className="my-1 w-1/5 px-3 mb-6">
                <ResponsiveImage
                  aspectRatio="1:1"
                  url={link.image.url}
                  altText={link.title}
                  media={[
                    {
                      width: 500,
                      useAsFallback: true,
                    },
                  ]}
                />
              </div>
            )}
            {!!singleColumn.length && (
              <ul className="w-1/5 px-3 mb-6">
                {singleColumn.map(childlink => (
                  <li
                    className="my-1"
                    key={`${link.id}-meganav-child-${childlink.id}`}
                  >
                    <Link
                      to={childlink.uri}
                      className="text-nav-lg uppercase w-100"
                    >
                      {childlink.title}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {megaNavColumns.map((childlink, i) => (
              <ul
                className="w-1/5 px-3 mb-6"
                key={`${link.id}-meganav-child-${childlink.id}`}
              >
                <li className="my-1">
                  <Link
                    to={childlink.uri}
                    className="text-nav-lg uppercase w-100"
                    onKeyDown={e => {
                      if (
                        i === megaNavColumns.length - 1 &&
                        !childlink.links?.length
                      ) {
                        tabOutHandler(e);
                      }
                    }}
                  >
                    {childlink.title}
                  </Link>
                </li>
                {childlink.links.map((grandchildlink, j) => (
                  <li
                    key={`${link.id}-${childlink.id}-grandchild-${grandchildlink.id}`}
                  >
                    <Link
                      to={grandchildlink.uri}
                      className="text-tiny"
                      onKeyDown={e => {
                        if (
                          i === megaNavColumns.length - 1 &&
                          j === childlink.links.length - 1
                        ) {
                          tabOutHandler(e);
                        }
                      }}
                    >
                      {grandchildlink.title}
                    </Link>
                  </li>
                ))}
              </ul>
            ))}
          </Container>
        </div>
      )}
    </li>
  );
};

NavItem.propTypes = {
  link: PropTypes.shape({
    title: PropTypes.string.isRequired,
    uri: PropTypes.string.isRequired,
    menu: PropTypes.array,
  }),
  depth: PropTypes.number,
  maxDepth: PropTypes.number,
  setMenuOpen: PropTypes.func,
};

export default NavItem;
